import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { endpoints } from '../api/endpoints';
import { LOGIN_SUCCESS_EVENT } from '../authEvents';
import {
  MENU_GROUP_CHILDREN,
  MENU_MODULE_MAP,
  isOwnerAdminOnlyMenu,
} from './menuModuleMap';
import { moduleGrantsAction, moduleGrantsView } from './permissionUtils';

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [ownerAdmin, setOwnerAdmin] = useState(false);
  const [accessRestricted, setAccessRestricted] = useState(false);
  const [accessMessage, setAccessMessage] = useState('');
  const [roleName, setRoleName] = useState(null);
  const [permissions, setPermissions] = useState({});

  const loadPermissions = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      setOwnerAdmin(false);
      setAccessRestricted(false);
      setPermissions({});
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.get(endpoints.users.mePermissions);
      setOwnerAdmin(Boolean(data.ownerAdmin));
      setAccessRestricted(Boolean(data.accessRestricted));
      setAccessMessage(data.accessMessage || '');
      setRoleName(data.roleName || null);
      setPermissions(data.permissions || {});
    } catch {
      setOwnerAdmin(false);
      setAccessRestricted(true);
      setAccessMessage(
        'Unable to load your access permissions. You can update your profile or sign in again.'
      );
      setPermissions({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  useEffect(() => {
    const onLogin = () => loadPermissions();
    window.addEventListener(LOGIN_SUCCESS_EVENT, onLogin);
    return () => window.removeEventListener(LOGIN_SUCCESS_EVENT, onLogin);
  }, [loadPermissions]);

  const can = useCallback(
    (moduleKey, action = 'VIEW') => {
      if (ownerAdmin) {
        return true;
      }
      if (accessRestricted) {
        return false;
      }
      return moduleGrantsAction(permissions, moduleKey, action);
    },
    [ownerAdmin, accessRestricted, permissions]
  );

  const canViewMenu = useCallback(
    (menuId) => {
      if (ownerAdmin) {
        return true;
      }
      if (accessRestricted) {
        return false;
      }
      if (menuId === 'Dashboard') {
        return false;
      }
      if (isOwnerAdminOnlyMenu(menuId)) {
        return false;
      }

      const groupChildren = MENU_GROUP_CHILDREN[menuId];
      if (groupChildren) {
        return groupChildren.some((childId) => {
          const moduleKey = MENU_MODULE_MAP[childId];
          return moduleKey && moduleGrantsView(permissions, moduleKey);
        });
      }

      const moduleKey = MENU_MODULE_MAP[menuId];
      if (!moduleKey) {
        return false;
      }
      return moduleGrantsView(permissions, moduleKey);
    },
    [ownerAdmin, accessRestricted, permissions]
  );

  const value = useMemo(
    () => ({
      loading,
      ownerAdmin,
      accessRestricted,
      accessMessage,
      roleName,
      permissions,
      can,
      canViewMenu,
      reloadPermissions: loadPermissions,
    }),
    [
      loading,
      ownerAdmin,
      accessRestricted,
      accessMessage,
      roleName,
      permissions,
      can,
      canViewMenu,
      loadPermissions,
    ]
  );

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>;
}

export function usePermissions() {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error('usePermissions must be used within PermissionProvider');
  }
  return ctx;
}
